import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Building2, MapPin, CheckCircle, ChevronRight, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelectHealthUnitPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    // Roles that don't need to select a health unit
    const noUnitRoles = ["admin", "secretario", "coordenador", "especialista"];
    if (noUnitRoles.includes(user.role)) {
      navigate("/dashboard");
      return;
    }
    if (user.health_unit_id) {
      navigate("/dashboard");
      return;
    }
    const municipality = user.municipality;
    const url = municipality
      ? `${API}/health-units?municipality=${encodeURIComponent(municipality)}`
      : `${API}/health-units`;
    axios.get(url, { withCredentials: true })
      .then(r => setUnits(r.data || []))
      .catch(() => setUnits([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const resp = await axios.patch(`${API}/users/me/health-unit`, { health_unit_id: selected }, { withCredentials: true });
      setUser(resp.data);
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const UNIT_TYPE_LABELS = {
    UBS: "Unidade Básica de Saúde",
    UBSF: "UBS Fluvial",
    ESF: "Estratégia Saúde da Família",
    "CAPS-AD": "CAPS Álcool e Drogas",
    hospital: "Hospital",
    outro: "Outro",
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="select-health-unit-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
            Sua Unidade de Saúde
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Selecione a unidade onde você atua em{" "}
            <strong>{user?.municipality || "seu município"}</strong>
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-border p-6 space-y-4 animate-fade-in">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-secondary/50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : units.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm font-semibold text-foreground">Nenhuma unidade cadastrada</p>
              <p className="text-xs text-muted-foreground mt-1">
                Aguarde o gestor do seu município cadastrar as unidades de saúde, ou entre em contato com o administrador.
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                className="mt-4 text-xs text-primary hover:underline"
                data-testid="skip-health-unit-btn"
              >
                Pular por enquanto
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                {units.length} unidade(s) disponível(is) em {user?.municipality}
              </p>
              <div className="space-y-2">
                {units.map(unit => (
                  <label
                    key={unit.unit_id}
                    className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                      selected === unit.unit_id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-secondary/50"
                    }`}
                    data-testid={`unit-option-${unit.unit_id}`}
                  >
                    <input
                      type="radio"
                      value={unit.unit_id}
                      checked={selected === unit.unit_id}
                      onChange={() => setSelected(unit.unit_id)}
                      className="accent-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${selected === unit.unit_id ? "text-primary" : "text-foreground"}`}>
                        {unit.name}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {UNIT_TYPE_LABELS[unit.unit_type] || unit.unit_type}
                        </span>
                        {unit.address && (
                          <>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="text-xs text-muted-foreground truncate">{unit.address}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {selected === unit.unit_id && (
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>

              <button
                onClick={handleSave}
                disabled={!selected || saving}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60 mt-2"
                data-testid="confirm-health-unit-btn"
              >
                <ChevronRight className="w-4 h-4" />
                {saving ? "Salvando..." : "Confirmar e Acessar Sistema"}
              </button>

              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-2.5 text-xs text-muted-foreground hover:text-foreground transition-all"
                data-testid="skip-unit-selection-btn"
              >
                Selecionar depois
              </button>
            </>
          )}
        </div>

        <div className="mt-4 bg-primary/5 border border-primary/20 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-foreground">
              A unidade selecionada determina quais pacientes você poderá acompanhar.
              Apenas uma unidade por profissional é permitida.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
